import { CircleHelp, Loader2, TriangleAlert, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';

export type ConfirmDialogVariant = 'default' | 'destructive';

const VARIANT_STYLE: Record<ConfirmDialogVariant, { icon: LucideIcon; iconClass: string }> = {
  default: { icon: CircleHelp, iconClass: ACCENT_MARK_CLASS },
  destructive: { icon: TriangleAlert, iconClass: 'bg-destructive/10 text-destructive' },
};

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string | undefined;
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: ConfirmDialogVariant;
  isPending?: boolean;
  pendingLabel?: string;
  onConfirm: () => void;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'default',
  isPending = false,
  pendingLabel,
  onConfirm,
}: ConfirmDialogProps) {
  const { icon: Icon, iconClass } = VARIANT_STYLE[confirmVariant];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="gap-5 p-5 shadow-2xl sm:max-w-md"
        overlayClassName="bg-foreground/30 supports-backdrop-filter:backdrop-blur-sm"
      >
        <DialogHeader className="pr-6">
          <div className={cn('flex gap-3', description ? 'items-start' : 'items-center')}>
            <span
              className={cn(
                'inline-flex size-7 shrink-0 items-center justify-center rounded-full',
                iconClass,
              )}
            >
              <Icon className="size-4" />
            </span>
            <div className="flex flex-col gap-1">
              <DialogTitle className="text-base leading-snug">{title}</DialogTitle>
              {description && <DialogDescription>{description}</DialogDescription>}
            </div>
          </div>
        </DialogHeader>
        {children}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {cancelLabel}
          </Button>
          <Button type="button" variant={confirmVariant} onClick={onConfirm} disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {pendingLabel ?? confirmLabel}
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
